import {
  Box,
  Button,
  FormControlLabel,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import SwitchControl from '../Shared/SwitchControl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import { Asset, AssetSearch, Dtc } from '@/types/asset_search';
import Mapbox from '../Mapbox/Mapbox';
import { InfoIcon, LotMapIcon } from '../Shared/Icons';
import {
  distanceBetweenTwoPoints,
  getDurationTime,
  getLocaleTime,
  getStorageDays,
  sortBatteryLevelComparator,
  sortDateComparator,
} from '@/utils';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { setAsset } from '@/features/asset/assetSlice';
import {
  setAssetTableFiltered,
  setAssetTableRefetch,
  setAssetTableSearchFilter,
} from '@/features/filters/filtersSlice';
import { useGetOverlayQuery } from '@/features/api/overlay.api';
import { RootState } from '@/app/store';
import { useDebounce } from '@/utils/hooks';
import { useGetVinSearchQuery } from '@/features/api/search.api';
import BaseTable from '../Shared/BaseTable';
import {
  getGridStringOperators,
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import i18n from '@/i18n/i18n';
import Spinner from '../Shared/Spinner';
import { ClientConfig } from '@/types/types';
import { Action, PluralEnum, Subject } from '@/types/enums';
import { CustomDetailPanelToggle } from '../Shared/CustomDetailPanelToggle';
import Can from '@/app/Auth/Can';
import BatteryLevel from '../Shared/BatteryLevel';
import { MapOverlay } from '../Shared/MapOverlay';
import MapSplitPane from '../Shared/MapSplitPane';
import { useGetAliasByIdQuery } from '@/features/api/alias.api';
import { ORG_MANHEIM_CODE } from '@/constants';
import { datatableMapContainer } from './AssetTable.style';
import { setSortedAssetPositionGlobal } from '@/features/user/userSlice';

type TableRowData = AssetSearch & Partial<Asset>;

export default function AssetTable() {
  const [t] = useTranslation();
  const dispatch = useAppDispatch();

  const siteMetadata: ClientConfig = useAppSelector(
    (state) => state.user.siteMetadata,
  );
  const userPositionGlobal = useAppSelector(
    (state: RootState) => state.user.userPosition,
  );
  const tetheredEnabledGlobal = useAppSelector(
    (state: RootState) => state.user.tetheredEnabled,
  );

  const sortedAssetPositionGlobal = useAppSelector(
    (state: RootState) => state.user.sortedAssetPosition,
  );

  const shouldRefetchAssetTable = useAppSelector(
    (state: RootState) => state.filters.assetTable.refetch,
  );

  const mapRef = useRef(null);

  const isDesktop = useMediaQuery('(min-width:900px)');
  const siteId = useAppSelector((state: RootState) => state.user.siteId);
  const urlSiteId = useParams()?.siteId?.toLowerCase() || '';

  const nameSite = useAppSelector((state) => state.user.siteMetadata.name);
  const navigate = useNavigate();
  const [sizes, setSizes] = useState([200, 200]);
  const [showMap, setShowMap] = useState<boolean>(true);
  const [showExport, setShowExport] = useState<boolean>(false);
  const [selectedAsset, setSelectedAsset] = useState<AssetSearch | null>(null);
  const [bestFitMap, setBestFitMap] = useState<boolean>(false);
  const [openMapOverlay, setOpenMapOverlay] = useState<boolean>(false);

  const [searchParams, setSearchParams] = useSearchParams();

  const search =
    useAppSelector((state: RootState) => state.filters.assetTable.search) ||
    sessionStorage.getItem('params') ||
    '';

  const filteredAssets = useAppSelector(
    (state) => state.filters.assetTable.assetFiltered,
  ) as AssetSearch[];
  const [showDataTableMobile, setShowDataTableMobile] =
    useState<boolean>(false);

  const { data: alias } = useGetAliasByIdQuery(
    { siteId: urlSiteId || siteId, aliasId: searchParams.get('zq') },
    { skip: !siteId || !searchParams.get('zq') },
  );

  useEffect(() => {
    if (!alias?.data) return;
    dispatch(setAssetTableSearchFilter(alias.data));
    searchParams.delete('zq');
    setSearchParams(searchParams);
  }, [alias]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const params = useDebounce(search, 0);

  sessionStorage.setItem('params', params);
  const skip = !params || params.length === 0;

  const {
    data: assetSearchData,
    isLoading,
    isSuccess,
    isError,
    isFetching: isFetchingAssets,
    refetch,
  } = useGetVinSearchQuery(
    {
      siteId: urlSiteId || siteId,
      params,
    },
    {
      skip,
    },
  );

  const {
    data: overlaysData,
    isLoading: isLoadingOverlays,
    isFetching: isFetchingOverlays,
  } = useGetOverlayQuery(urlSiteId || siteId, {
    skip: !siteId || !assetSearchData || !Can(Action.READ, Subject.geofence),
  });

  const handlePreviousOrNextAsset = (action: 'previous' | 'next') => {
    if (filteredAssets.length > 1) {
      const selectedAssetIndex = filteredAssets.findIndex(
        (filteredAsset) =>
          filteredAsset.asset_uuid === selectedAsset?.asset_uuid,
      );

      let newIndex = 0;

      switch (action) {
        case 'next':
          if (selectedAssetIndex + 1 === filteredAssets.length) {
            newIndex = 0;
          } else {
            newIndex = selectedAssetIndex + 1;
          }
          break;

        case 'previous':
          if (selectedAssetIndex === 0) {
            newIndex = filteredAssets.length - 1;
          } else {
            newIndex = selectedAssetIndex - 1;
          }
          break;

        default:
          break;
      }

      handleSummaryScreen(filteredAssets[`${newIndex}`]);
    }
  };

  const handleSummaryScreen = (selectedRowData: AssetSearch) => {
    // if (mapRef.current && selectedRowData.lon && selectedRowData.lat) {
    //   const map: MapRef = mapRef.current;
    //   map.flyTo({
    //     center: [selectedRowData.lon, selectedRowData.lat],
    //   });
    // }
    setBestFitMap(false);
    setSelectedAsset(selectedRowData);
    if (isDesktop || !showDataTableMobile) {
      setShowDataTableMobile(false);
      setOpenMapOverlay(true);
    }
  };

  useEffect(() => {
    if (!isDesktop) {
      setShowDataTableMobile(false);
    }
  }, [isDesktop]);

  useEffect(() => {
    if (shouldRefetchAssetTable && filteredAssets.length > 0) {
      refetch();
      dispatch(setAssetTableRefetch(false));
    }
  }, [shouldRefetchAssetTable]);

  useEffect(() => {
    if (!assetSearchData || params.length === 0) return;
    dispatch(setAssetTableFiltered(assetSearchData));
    dispatch(setAssetTableRefetch(false));
  }, [assetSearchData, isDesktop]);

  useEffect(() => {
    if (userPositionGlobal === null) {
      dispatch(setSortedAssetPositionGlobal(false));
    }
    // do nothing when tethered is disabled
    if (sortedAssetPositionGlobal && !tetheredEnabledGlobal) {
      return;
    }

    if (!filteredAssets || params.length === 0) return;

    let filteredAssetSearchDataSorted = [...filteredAssets];
    if (
      tetheredEnabledGlobal &&
      userPositionGlobal &&
      !sortedAssetPositionGlobal
    ) {
      const positionGlobal = {
        lat: userPositionGlobal.lat,
        lon: userPositionGlobal.lon,
      };
      // based on user position, iterate over the assets and sort them by distance
      filteredAssetSearchDataSorted = filteredAssetSearchDataSorted.sort(
        (a, b) => {
          const distanceA = distanceBetweenTwoPoints(positionGlobal, {
            lat: a.lat ?? 0,
            lon: a.lon ?? 0,
          });
          const distanceB = distanceBetweenTwoPoints(positionGlobal, {
            lat: b.lat ?? 0,
            lon: b.lon ?? 0,
          });
          return distanceA - distanceB;
        },
      );
      dispatch(setSortedAssetPositionGlobal(true));
      dispatch(setAssetTableFiltered(filteredAssetSearchDataSorted));
    }
    if (
      tetheredEnabledGlobal &&
      filteredAssetSearchDataSorted.length > 0 &&
      userPositionGlobal &&
      !sortedAssetPositionGlobal
    ) {
      setSelectedAsset(filteredAssetSearchDataSorted[0] as AssetSearch);
    } else if (
      (filteredAssetSearchDataSorted.length > 0 && selectedAsset === null) ||
      (filteredAssetSearchDataSorted.length >= 1 && !tetheredEnabledGlobal)
    ) {
      setSelectedAsset(filteredAssetSearchDataSorted[0] as AssetSearch);
    }

    setOpenMapOverlay(true);
    // dispatch(setAssetTableFiltered(filteredAssetSearchDataSorted));
    setBestFitMap(true);
  }, [filteredAssets, isDesktop, tetheredEnabledGlobal, userPositionGlobal]);

  useEffect(() => {
    const queryParams: string = Object.fromEntries([...searchParams])?.q;
    if (!queryParams || queryParams === '') return;
    dispatch(setAssetTableSearchFilter(queryParams));
    searchParams.delete('q');
    setSearchParams(searchParams);
    setBestFitMap(true);
  }, [searchParams]);

  const columnsTable: GridColDef[] = [
    {
      field: 'asset_uuid',
      headerName: 'vin',
      hideable: false,
      width: 180,
      renderCell: (params: GridRenderCellParams<TableRowData>) => {
        return (
          <Tooltip
            title={params.row.asset_uuid}
            onClick={() => {
              if (isDesktop && siteMetadata.orgCode !== ORG_MANHEIM_CODE) {
                dispatch(setAsset(params.row));
                navigate(`/${siteId}/assets/${params.row.asset_uuid}`);
              } else {
                handleSummaryScreen(params.row);
              }
            }}
          >
            <span
              data-testid={`vin-${params.row.asset_uuid}`}
              className={isDesktop ? 'table-cell-trucate' : ''}
            >
              {params.row.asset_uuid}
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: 'zone_name',
      headerName: 'zone',
      flex: 1,
      minWidth: 100,
      renderCell: (params: GridRenderCellParams<TableRowData>) => {
        return (
          <Tooltip title={params.row.zone_name}>
            <span className="table-cell-trucate">{params.row.zone_name}</span>
          </Tooltip>
        );
      },
    },
    {
      field: 'vehicle_make',
      headerName: 'make',
      width: 120,
    },
    Can(Action.READ, Subject.vehicle_battery) && {
      field: 'vehicle_level',
      width: 180,
      headerName: 'battery',
      sortComparator: sortBatteryLevelComparator,
      renderCell: (params: GridRenderCellParams<TableRowData>) => {
        return <BatteryLevel asset={params.row} />;
      },
    },
    {
      field: 'vehicle_model',
      headerName: 'model',
      width: 130,
      valueGetter: (_value: string | undefined, row: TableRowData) => {
        return row.vehicle_model || row.vehicle_model_code;
      },
      renderCell: (params: GridRenderCellParams<TableRowData>) => {
        return (
          <Tooltip
            title={params.row.vehicle_model || params.row.vehicle_model_code}
          >
            <span className="table-cell-trucate">
              {params.row.vehicle_model || params.row.vehicle_model_code}
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: 'vehicle_year',
      headerName: 'year',
      width: 100,
    },
    siteMetadata.orgCode == ORG_MANHEIM_CODE && {
      field: 'sale_year',
      headerName: 'saleYear',
      width: 100,
    },
    siteMetadata.orgCode == ORG_MANHEIM_CODE && {
      field: 'sale_number',
      headerName: 'saleNumber',
      width: 120,
    },
    siteMetadata.orgCode == ORG_MANHEIM_CODE && {
      field: 'sale_lane',
      headerName: 'saleLane',
      width: 100,
    },
    siteMetadata.orgCode == ORG_MANHEIM_CODE && {
      field: 'sale_run',
      headerName: 'saleRun',
      width: 100,
    },
    {
      field: 'assoc_timestamp',
      headerName: 'associationTimestamp',
      width: 160,
      valueFormatter: (value: string | number) => {
        return getLocaleTime(value);
      },
      sortComparator: sortDateComparator,
    },
    {
      field: 'time_in_zone',
      headerName: 'timeInZone',
      width: 120,
      valueFormatter: (value: number | null) => {
        return value ? `${getDurationTime(value, t) ?? ''}` : '';
      },
      valueGetter: (_value: string | undefined, row: TableRowData) => {
        const { zone_name, time_in_zone } = row;
        if (!zone_name || !time_in_zone) return null;
        return parseFloat(time_in_zone) * 1000;
      },
    },
    Can(Action.READ, Subject.stock_number) && {
      field: 'vehicle_stock_number',
      headerName: 'stock',
    },
    Can(Action.READ, Subject.table_dtc) && {
      field: 'dtc',
      headerName: 'dtcCodes',
      width: 200,
      renderHeader: () => (
        <Box>
          <strong>{t('assetTable.dtcCodes')}</strong>
          {Can(Action.READ, Subject.disclaimer) && (
            <Tooltip
              data-testid="dtc-disclaimer"
              title={
                <>
                  <Typography
                    sx={() => ({
                      fontSize: '14px',
                      fontWeight: '600',
                    })}
                  >
                    {t('disclaimerDialog.dtcDisclaimerTitle')}
                    <br />
                    <br />
                  </Typography>
                  <Typography
                    sx={() => ({
                      fontSize: '14px',
                    })}
                  >
                    {t('disclaimerDialog.dtcDisclaimer', {
                      SITE_ID: nameSite,
                    })}
                    <br />
                    <br />
                  </Typography>
                </>
              }
            >
              <span>
                <InfoIcon
                  style={{
                    width: '16px',
                    height: '22px',
                    marginLeft: '10px',
                  }}
                  viewBox="0 0 20 20"
                />
              </span>
            </Tooltip>
          )}
        </Box>
      ),
      filterOperators: getGridStringOperators(),
      valueGetter: (_value: string | undefined, row: TableRowData) => {
        if (row?.has_dtc_optedout) {
          return false;
        }

        return Array.from(new Set(row?.dtc?.map((dtc: Dtc) => dtc.code))).join(
          ',',
        );
      },
      renderCell: (params: GridRenderCellParams<TableRowData>) => {
        if (params.row?.has_dtc_optedout) {
          return <span>{t('assetTable.dtcSellerOptedOut')}</span>;
        }

        if (params.row?.dtc?.length == 0 && !params.row?.has_dtc_optedout) {
          return <span>--</span>;
        }

        const dtcCodes = Array.from(
          new Set(params.row?.dtc?.map((dtc: Dtc) => dtc.code)),
        ).join(',');
        return params.row?.dtc && params.row.dtc.length > 0 ? (
          <Tooltip title={dtcCodes}>
            <span className="table-cell-trucate">{dtcCodes}</span>
          </Tooltip>
        ) : null;
      },
    },
    Can(Action.READ, Subject.seller_id) && {
      field: 'seller_id',
      headerName: 'sellerId',
      width: 100,
    },
    {
      field: 'make',
      headerName: 'make',
      width: 120,
    },
    {
      field: 'model',
      headerName: 'model',
      width: 120,
    },
    {
      field: 'color',
      headerName: 'color',
      width: 130,
    },
    {
      field: 'time_since_assoc',
      headerName: 'timeSinceAssoc',
      width: 130,
      valueFormatter: (value: number) => {
        return value ? `${getDurationTime(value, t) ?? ''}` : '';
      },
      valueGetter: (_value: string | undefined, row: TableRowData) => {
        return row.time_since_assoc ? parseFloat(row.time_since_assoc) : 0;
      },
    },
    {
      field: 'code',
      headerName: 'code',
      width: 130,
    },
    {
      field: 'storageDays',
      headerName: 'storageDays',
      width: 130,
      valueGetter: (_value: string | undefined, row: TableRowData) => {
        return getStorageDays(row.time_since_assoc || '', row.freeDays || 0);
      },
    },
    {
      field: 'additionalServices',
      headerName: 'additionalServices',
      width: 140,
    },
    {
      field: 'remarks',
      headerName: 'remarks',
      width: 130,
    },
    !isDesktop && {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: window.innerWidth - 26 - 180,
      cellClassName: 'custom-detail-column',
      renderCell: (params: GridRenderCellParams<TableRowData>) => (
        <CustomDetailPanelToggle id={params.id} value={params.value} />
      ),
    },
  ].filter(Boolean) as GridColDef[];

  const columns = useMemo(() => {
    return columnsTable
      .filter((column) => {
        // for custom valueGetter use column.valueGetter
        return filteredAssets.some((asset: AssetSearch) => {
          if (isDesktop) {
            if (column.field === 'vehicle_model') {
              const modelValue =
                asset.vehicle_model || asset.vehicle_model_code;
              return modelValue !== null && modelValue !== undefined;
            } else if (column.field === 'dtc') {
              return (
                asset.has_dtc_optedout || (asset.dtc && asset.dtc.length > 0)
              );
            } else {
              const fieldValue = asset[column.field as keyof AssetSearch];
              return ![null, undefined, ''].includes(fieldValue as any);
            }
          } else {
            return ['asset_uuid', '__detail_panel_toggle__'].includes(
              column.field,
            );
          }
        });
      })
      .map((column) => ({
        ...column,
        headerName: t(`assetTable.${column.headerName}`),
      }));
  }, [filteredAssets, t, i18n.language, isDesktop]);

  useEffect(() => {
    if (filteredAssets && filteredAssets.length > 0) {
      setShowMap(true);
      setShowExport(true);
    } else {
      setShowMap(false);
      setShowExport(false);
    }
  }, [filteredAssets]);

  useEffect(() => {
    if (isFetchingAssets) {
      dispatch(setSortedAssetPositionGlobal(false));
    }
  }, [isFetchingAssets]);

  if (isLoading || isFetchingAssets || isLoadingOverlays || isFetchingOverlays)
    return <Spinner />;

  return (
    <Box data-testid="asset-page-container" sx={datatableMapContainer}>
      {isDesktop && filteredAssets && filteredAssets.length > 0 && (
        <FormControlLabel
          data-testid="asset-table-toggle-map"
          value="Map"
          sx={(theme) => ({
            display: 'flex',
            flexDirection: 'row-reverse',
            alignSelf: 'flex-end',
            marginBottom: '10px',
            marginRight: 0,
            color: theme.palette.text.primary,
          })}
          control={
            <SwitchControl
              checked={showMap}
              setChecked={setShowMap}
              isDisable={!filteredAssets || filteredAssets?.length === 0}
            />
          }
          label={t('assetTable.map')}
          componentsProps={{
            typography: {
              marginRight: '12px',
            },
          }}
        />
      )}
      <MapSplitPane
        isDesktop={isDesktop}
        panes={[
          <Grid
            size={{ xs: 12 }}
            key={0}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              height: '100%',
            }}
          >
            <Paper
              sx={{
                width: '100%',
                height: '100%',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <BaseTable
                columns={columns}
                rows={filteredAssets}
                handleShowDataTableMobile={setShowDataTableMobile}
                disableVirtualization={true}
                isSuccess={isSuccess}
                isError={isError}
                isFetchingAssets={isFetchingAssets}
                handleRow={(asset: AssetSearch) => handleSummaryScreen(asset)}
                getRowId={(params) => params.asset_uuid}
                title={t(`assetType.${siteMetadata.assetType}`, {
                  count: PluralEnum.PLURAL,
                })}
                showExport={showExport}
                componentInsideTable={
                  showDataTableMobile ||
                  filteredAssets.length === 0 ||
                  isDesktop ? null : (
                    <>
                      <Box
                        data-testid="mapbox-container-mobile"
                        className="mapbox-container-mobile"
                      >
                        <Mapbox
                          data={filteredAssets}
                          items={overlaysData?.items}
                          selected={selectedAsset}
                          setSelected={(asset: AssetSearch) => {
                            handleSummaryScreen({
                              ...asset,
                              dtc:
                                asset?.dtc && typeof asset.dtc === 'string'
                                  ? JSON.parse(asset.dtc)
                                  : asset?.dtc || [],
                            });
                          }}
                          bestFitZoom={bestFitMap}
                          mapRef={mapRef}
                        />
                      </Box>
                      {openMapOverlay && selectedAsset && (
                        <MapOverlay
                          asset={selectedAsset}
                          setSelectedAsset={setSelectedAsset}
                          onClose={() => setOpenMapOverlay(false)}
                          handlePreviousOrNextAsset={handlePreviousOrNextAsset}
                        />
                      )}
                    </>
                  )
                }
                search={search}
              />
            </Paper>
          </Grid>,
          ...(showMap && isDesktop
            ? [
                <Grid
                  container
                  spacing={0.5}
                  display="flex"
                  flexGrow={1}
                  key={1}
                  height={'100%'}
                >
                  {showMap && (
                    <Grid
                      key={1}
                      size={{ xs: 12 }}
                      sx={{
                        display: showMap ? 'flex' : 'none',
                        flexFlow: 'column',
                        minHeight: { xs: '0px', md: '80vh' },
                        width: '100%',
                        maxHeight: {
                          xs: '0px!important',
                          md: '100%!important',
                        },
                        maxWidth: { md: '100%!important' },
                        position: 'relative',
                      }}
                      data-testid="mapbox-container"
                    >
                      {showMap &&
                        isDesktop &&
                        filteredAssets &&
                        filteredAssets.length > 0 && (
                          <Mapbox
                            mapRef={mapRef}
                            data={filteredAssets}
                            items={overlaysData?.items}
                            selected={selectedAsset}
                            bestFitZoom={bestFitMap}
                            setSelected={(asset: AssetSearch) => {
                              handleSummaryScreen({
                                ...asset,
                                dtc:
                                  asset?.dtc && typeof asset.dtc === 'string'
                                    ? JSON.parse(asset.dtc)
                                    : asset?.dtc || [],
                              });
                            }}
                            resized={sizes}
                          />
                        )}
                      {openMapOverlay && selectedAsset && (
                        <MapOverlay
                          asset={selectedAsset}
                          setSelectedAsset={setSelectedAsset}
                          onClose={() => setOpenMapOverlay(false)}
                          handlePreviousOrNextAsset={handlePreviousOrNextAsset}
                        />
                      )}
                    </Grid>
                  )}
                </Grid>,
              ]
            : []),
        ]}
        setSizes={setSizes}
      />
      {!isDesktop && filteredAssets && filteredAssets.length > 0 && (
        <Button
          sx={(theme) => ({
            position: 'fixed',
            bottom: '90px',
            left: 0,
            right: 0,
            margin: '0 auto',
            padding: '8px',
            borderRadius: '12px',
            background: theme.palette.background.paper,
            width: '128px',
            zIndex: 2,
            boxShadow:
              '0px 4px 8px 3px rgba(0, 0, 0, 0.15), 0px 3px 3px rgba(0, 0, 0, 0.3)',
            ':hover': {
              background: theme.palette.background.paper,
            },
          })}
          data-testid="asset-table-mobile-switch-screen"
          onClick={() => setShowDataTableMobile(!showDataTableMobile)}
        >
          <Typography
            sx={(theme) => ({
              flex: 1,
              fontSize: '14px',
              fontWeight: '600',
              color: theme.palette.text.primary,
              textTransform: 'capitalize',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            })}
          >
            <LotMapIcon viewBox="0 0 20 20" style={{ marginRight: '10px' }} />
            {`${t('assetTable.view')} ${
              showDataTableMobile ? t('assetTable.map') : t('assetTable.table')
            }`}
          </Typography>
        </Button>
      )}
    </Box>
  );
}
